// File → base64 helpers for the extract-invoice / extract-po Lambda calls.
//
// History: invoice + PO drawers used to do
//   btoa(String.fromCharCode(...new Uint8Array(buf)))
// which spreads every byte as an argument and blows V8's argument-count
// limit on multi-MB files — surfaces as "Maximum call stack size
// exceeded" in the browser. Reproduced locally on a 1.8 MB RTF.
//
// FileReader.readAsDataURL is iterative inside the browser engine and
// returns a `data:<mime>;base64,<payload>` URL we can split on the
// comma. Works for any file size up to the browser's blob limit
// (typically 100s of MB).
//
// MAX_BYTES is the client-side cap: AWS Lambda Function URLs (BUFFERED
// mode) have a 6 MB request body limit, base64 inflates by ~33% plus
// JSON envelope, so we cap raw input at 4.5 MB to leave headroom.
export const MAX_BYTES = 4_500_000;

export class FileTooLargeError extends Error {
  constructor(size, max = MAX_BYTES) {
    const mb = (n) => (n / 1024 / 1024).toFixed(1);
    super(`File is ${mb(size)} MB — over the ${mb(max)} MB limit. Try a smaller file or split the invoice.`);
    this.name = 'FileTooLargeError';
    this.size = size;
    this.max = max;
  }
}

/**
 * Read a File/Blob and return its base64 payload (no `data:` prefix).
 * Throws FileTooLargeError if the file is over the cap.
 */
export function fileToBase64(file) {
  if (file.size > MAX_BYTES) {
    return Promise.reject(new FileTooLargeError(file.size));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader result'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
